import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { db, COLLECTIONS } from '../firebase/config';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { 
  Sun, 
  Target, 
  AlertTriangle, 
  Heart, 
  CheckSquare,
  Calendar,
  Clock,
  Users,
  Save,
  ArrowRight,
  Moon,
  XCircle,
  Lightbulb,
  TrendingUp,
  CheckCircle,
  CalendarDays,
  CalendarRange,
  CalendarClock,
  BarChart,
  Briefcase
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// קומפוננטה להצגת סיכום פתיחת מחזור
const PeriodOpeningSummaryView = ({ periodOpening, tasks, onContinueToSummary, onBackToEdit, onChangePeriod }) => {  // מציאת המשימות שנבחרו
  const selectedTasksDetails = tasks.filter(task => 
    periodOpening.selectedTasks?.includes(task.id)
  );

  const getMoodEmoji = (mood) => {
    if (mood >= 4) return '😊';
    if (mood >= 3) return '😐';
    return '😞';
  };

  const getMoodText = (mood) => {
    if (mood >= 4) return 'מצוין';
    if (mood >= 3) return 'בסדר';
    return 'לא כל כך טוב';
  };

  const getPeriodTitle = (type) => {
    switch(type) {
      case 'day': return 'היום';
      case 'week': return 'השבוע';
      case 'month': return 'החודש';
      case 'quarter': return 'הרבעון';
      case 'year': return 'השנה';
      default: return 'התקופה';
    }
  };

  const getPeriodIcon = (type) => {
    switch(type) {
      case 'day': return <Sun className="w-12 h-12" />;
      case 'week': return <CalendarDays className="w-12 h-12" />;
      case 'month': return <Calendar className="w-12 h-12" />;
      case 'quarter': return <CalendarRange className="w-12 h-12" />;
      case 'year': return <CalendarClock className="w-12 h-12" />;
      default: return <Calendar className="w-12 h-12" />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* כותרת */}
      <div className="bg-gradient-to-r from-orange-500 to-yellow-500 rounded-xl p-8 text-white mb-8">
        <div className="flex items-center gap-4">
          {getPeriodIcon(periodOpening.periodType)}
          <div>
            <h2 className="text-3xl font-bold mb-2">
              סיכום פתיחת {getPeriodTitle(periodOpening.periodType)} שלך
            </h2>
            <p className="text-orange-100">
              {new Date().toLocaleDateString('he-IL', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
        </div>
      </div>

      {/* תוכן הסיכום */}
      <div className="space-y-6">
        
        {/* מצב רוח ואנרגיה - רק ליום */}
        {periodOpening.periodType === 'day' && periodOpening.moodEnergy && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-600" />
              מצב רוח ואנרגיה
            </h3>
            <div className="flex items-center gap-4">
              <div className="text-4xl">{getMoodEmoji(periodOpening.moodEnergy)}</div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {periodOpening.moodEnergy}/5
                </div>
                <div className="text-gray-600">
                  אתה מרגיש {getMoodText(periodOpening.moodEnergy)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* סקירה כללית */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            התכנון שלך ל{getPeriodTitle(periodOpening.periodType)}
          </h3>
          <p className="text-gray-700 bg-blue-50 p-4 rounded-lg">
            {periodOpening.overview}
          </p>
        </div>

        {/* יעדים מרכזיים */}
        {(periodOpening.goals || selectedTasksDetails.length > 0) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-green-600" />
              יעדים מרכזיים ל{getPeriodTitle(periodOpening.periodType)}
            </h3>
            
            {periodOpening.goals && periodOpening.goals.length > 0 && (
              <div className="space-y-3 mb-4">
                <h4 className="font-medium text-gray-700">יעדים שהוגדרו:</h4>
                {periodOpening.goals.map((goal, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                    <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <p className="flex-1 text-gray-900">{goal}</p>
                  </div>
                ))}
              </div>
            )}
            
            {selectedTasksDetails.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-gray-700">משימות נבחרות:</h4>
                {selectedTasksDetails.map((task, index) => (
                  <div key={task.id} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{task.title}</p>
                      <p className="text-sm text-gray-600">
                        {task.priority} • {task.status}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* פגישות ודדליינים - ליום ושבוע */}
        {(periodOpening.periodType === 'day' || periodOpening.periodType === 'week') && 
         (periodOpening.meetings || periodOpening.deadlines) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {periodOpening.meetings && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  פגישות מתוכננות
                </h3>
                <p className="text-gray-700 bg-purple-50 p-4 rounded-lg">
                  {periodOpening.meetings}
                </p>
              </div>
            )}
            
            {periodOpening.deadlines && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-red-600" />
                  דדליינים חשובים
                </h3>
                <p className="text-gray-700 bg-red-50 p-4 rounded-lg">
                  {periodOpening.deadlines}
                </p>
              </div>
            )}
          </div>
        )}

        {/* פרויקטים מרכזיים - לחודש ומעלה */}
        {(periodOpening.periodType === 'month' || periodOpening.periodType === 'quarter' || periodOpening.periodType === 'year') && 
         periodOpening.projects && periodOpening.projects.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-indigo-600" />
              פרויקטים מרכזיים
            </h3>
            <div className="space-y-3">
              {periodOpening.projects.map((project, index) => (
                <div key={index} className="p-3 bg-indigo-50 rounded-lg">
                  <p className="text-gray-900">{project}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* KPIs - לרבעון ושנה */}
        {(periodOpening.periodType === 'quarter' || periodOpening.periodType === 'year') && 
         periodOpening.kpis && periodOpening.kpis.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart className="w-5 h-5 text-blue-600" />
              מדדי הצלחה (KPIs)
            </h3>
            <div className="space-y-3">
              {periodOpening.kpis.map((kpi, index) => (
                <div key={index} className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-gray-900">{kpi}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* חסמים */}
        {periodOpening.blockers && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              חסמים ואתגרים צפויים
            </h3>
            <p className="text-gray-700 bg-red-50 p-4 rounded-lg">
              {periodOpening.blockers}
            </p>
          </div>
        )}

        {/* כפתורי פעולה */}
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
          <div className="text-center mb-4">
            <p className="text-lg text-gray-700">
              מוכן להתחיל את {getPeriodTitle(periodOpening.periodType)}? 🚀
            </p>
            <p className="text-sm text-gray-500 mt-1">
              בסוף {getPeriodTitle(periodOpening.periodType)} תוכל למלא סיכום ולראות מה הספקת
            </p>
          </div>
          
          <div className="flex gap-4 justify-center">
            <button
              onClick={onContinueToSummary}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:from-blue-600 hover:to-purple-600 font-medium flex items-center gap-2"
            >
              <Moon className="w-5 h-5" />
              מלא סיכום {getPeriodTitle(periodOpening.periodType)} עכשיו
            </button>
            
            <button
              onClick={onBackToEdit}
              className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
            >
              ערוך פתיחת {getPeriodTitle(periodOpening.periodType)}
            </button>
          </div>
          {/* כפתורי מעבר מהיר לתקופות אחרות */}
<div className="mt-6 pt-6 border-t border-gray-200">
  <p className="text-sm text-gray-600 mb-3 text-center">רוצה למלא פתיחה לתקופה נוספת?</p>
  <div className="flex gap-2 justify-center flex-wrap">
    {['day', 'week', 'month', 'quarter', 'year'].filter(type => type !== periodOpening.periodType).map(type => (
      <button
        key={type}
        onClick={() => onChangePeriod(type)}
        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
      >
        {type === 'day' && <Sun className="w-4 h-4" />}
        {type === 'week' && <CalendarDays className="w-4 h-4" />}
        {type === 'month' && <Calendar className="w-4 h-4" />}
        {type === 'quarter' && <CalendarRange className="w-4 h-4" />}
        {type === 'year' && <CalendarClock className="w-4 h-4" />}
        פתיחת {getPeriodTitle(type)}
      </button>
    ))}
  </div>
</div>
        </div>
      </div>
    </div>
  );
};

// קומפוננטה ראשית
const PeriodOpening = () => {
  const { currentUser } = useAuth();
  const { tasks, projects } = useData();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [hasOpenedToday, setHasOpenedToday] = useState(false);
  const [todayOpening, setTodayOpening] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [showSummaryView, setShowSummaryView] = useState(false);
  const [periodType, setPeriodType] = useState('day'); // day, week, month, quarter, year
  
  const [formData, setFormData] = useState({
    overview: '',
    blockers: '',
    moodEnergy: 3,
    selectedTasks: [],
    meetings: '',
    deadlines: '',
    goals: [],
    projects: [],
    kpis: [],
    priorities: [],
    weeklyPlan: {},
    monthlyPlan: {},
    quarterlyPlan: {},
    yearlyPlan: {}
  });

  // בדיקה אם כבר פתח מחזור היום
  useEffect(() => {
    checkTodayOpening();
  }, [currentUser, periodType]);

  const checkTodayOpening = async () => {
    if (!currentUser) return;
    
    try {
      const today = new Date();
      const todayString = today.toDateString();
      
      const userId = currentUser.uid || currentUser.id;
      
      const q = query(
        collection(db, COLLECTIONS.PERIOD_OPENINGS || COLLECTIONS.DAY_OPENINGS),
        where('userId', '==', userId),
        where('periodType', '==', periodType)
      );
      
      const querySnapshot = await getDocs(q);
      
      // אפס את המצב לפני הבדיקה
      setHasOpenedToday(false);
      setTodayOpening(null);
      
      if (!querySnapshot.empty) {
        for (const doc of querySnapshot.docs) {
          const docData = doc.data();
          let docDate = null;
          
          if (docData.date) {
            if (docData.date.toDate) {
              docDate = docData.date.toDate();
            } else if (docData.date instanceof Date) {
              docDate = docData.date;
            } else if (typeof docData.date === 'string') {
              docDate = new Date(docData.date);
            }
          }
          
          // בדיקה לפי סוג התקופה
          let isCurrentPeriod = false;
          if (periodType === 'day' && docDate && docDate.toDateString() === todayString) {
            isCurrentPeriod = true;
          } else if (periodType === 'week' && isInCurrentWeek(docDate)) {
            isCurrentPeriod = true;
          } else if (periodType === 'month' && isInCurrentMonth(docDate)) {
            isCurrentPeriod = true;
          } else if (periodType === 'quarter' && isInCurrentQuarter(docDate)) {
            isCurrentPeriod = true;
          } else if (periodType === 'year' && isInCurrentYear(docDate)) {
            isCurrentPeriod = true;
          }
          
          if (isCurrentPeriod) {
            setHasOpenedToday(true);
            setTodayOpening({ id: doc.id, ...docData });
            setFormData({
              overview: docData.overview || docData.dailyOverview || '',
              blockers: docData.blockers || '',
              moodEnergy: docData.moodEnergy || 3,
              selectedTasks: docData.selectedTasks || [],
              meetings: docData.meetings || '',
              deadlines: docData.deadlines || '',
              goals: docData.goals || [],
              projects: docData.projects || [],
              kpis: docData.kpis || [],
              priorities: docData.priorities || [],
              weeklyPlan: docData.weeklyPlan || {},
              monthlyPlan: docData.monthlyPlan || {},
              quarterlyPlan: docData.quarterlyPlan || {},
              yearlyPlan: docData.yearlyPlan || {}
            });
            break;
          }
        }
      }
    } catch (error) {
      console.error('Error checking period opening:', error);
    } finally {
      setCheckingExisting(false);
    }
  };

  // פונקציות עזר לבדיקת תקופות
  const isInCurrentWeek = (date) => {
    const now = new Date();
    const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
    const weekEnd = new Date(now.setDate(now.getDate() - now.getDay() + 6));
    return date >= weekStart && date <= weekEnd;
  };

  const isInCurrentMonth = (date) => {
    const now = new Date();
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  };

  const isInCurrentQuarter = (date) => {
    const now = new Date();
    const currentQuarter = Math.floor(now.getMonth() / 3);
    const dateQuarter = Math.floor(date.getMonth() / 3);
    return dateQuarter === currentQuarter && date.getFullYear() === now.getFullYear();
  };

  const isInCurrentYear = (date) => {
    return date.getFullYear() === new Date().getFullYear();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTaskToggle = (taskId) => {
    setFormData(prev => ({
      ...prev,
      selectedTasks: prev.selectedTasks.includes(taskId)
        ? prev.selectedTasks.filter(id => id !== taskId)
        : [...prev.selectedTasks, taskId]
    }));
  };

  const handleAddToList = (field, value) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value]
      }));
    }
  };

  const handleRemoveFromList = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userId = currentUser.uid || currentUser.id;
      
      const openingData = {
        userId: userId,
        userName: currentUser.name,
        userRole: currentUser.role,
        departmentId: currentUser.departmentId || null,
        teamId: currentUser.teamId || null,
        date: new Date(),
        periodType: periodType,
        overview: formData.overview,
        blockers: formData.blockers,
        createdAt: serverTimestamp()
      };

      // הוסף שדות ספציפיים לפי סוג התקופה
      if (periodType === 'day') {
        openingData.moodEnergy = formData.moodEnergy;
        openingData.selectedTasks = formData.selectedTasks;
        openingData.meetings = formData.meetings;
        openingData.deadlines = formData.deadlines;
      }
      
      if (periodType === 'week') {
        openingData.goals = formData.goals;
        openingData.priorities = formData.priorities;
        openingData.meetings = formData.meetings;
        openingData.deadlines = formData.deadlines;
        openingData.weeklyPlan = formData.weeklyPlan;
      }
      
      if (periodType === 'month') {
        openingData.goals = formData.goals;
        openingData.projects = formData.projects;
        openingData.kpis = formData.kpis;
        openingData.monthlyPlan = formData.monthlyPlan;
      }
      
      if (periodType === 'quarter') {
        openingData.goals = formData.goals;
        openingData.projects = formData.projects;
        openingData.kpis = formData.kpis;
        openingData.quarterlyPlan = formData.quarterlyPlan;
      }
      
      if (periodType === 'year') {
        openingData.goals = formData.goals;
        openingData.projects = formData.projects;
        openingData.kpis = formData.kpis;
        openingData.yearlyPlan = formData.yearlyPlan;
      }

      const collectionName = COLLECTIONS.PERIOD_OPENINGS || COLLECTIONS.DAY_OPENINGS;
      const docRef = await addDoc(collection(db, collectionName), openingData);
      
      setTodayOpening({ id: docRef.id, ...openingData });
      setHasOpenedToday(true);
      
      alert(`פתיחת ${getPeriodTitle(periodType)} נשמרה בהצלחה! 🌅`);
      
    } catch (error) {
      console.error('Error saving period opening:', error);
      alert('שגיאה בשמירת פתיחת המחזור');
    } finally {
      setLoading(false);
    }
  };

  const getPeriodTitle = (type) => {
    switch(type) {
      case 'day': return 'יום';
      case 'week': return 'שבוע';
      case 'month': return 'חודש';
      case 'quarter': return 'רבעון';
      case 'year': return 'שנה';
      default: return 'תקופה';
    }
  };

  const getPeriodIcon = (type) => {
    switch(type) {
      case 'day': return <Sun className="w-8 h-8" />;
      case 'week': return <CalendarDays className="w-8 h-8" />;
      case 'month': return <Calendar className="w-8 h-8" />;
      case 'quarter': return <CalendarRange className="w-8 h-8" />;
      case 'year': return <CalendarClock className="w-8 h-8" />;
      default: return <Calendar className="w-8 h-8" />;
    }
  };

  // משימות רלוונטיות למשתמש
  const allRelevantTasks = tasks.filter(task => 
    (task.assigneeId === currentUser?.id || task.createdBy === currentUser?.id) && 
    task.status !== 'הושלם'
  );

  // חלוקה לקטגוריות
  const personalTasks = allRelevantTasks.filter(task => task.scope === 'personal');
  const managerialTasks = allRelevantTasks.filter(task => task.scope === 'managerial' || task.scope === 'department');

  if (checkingExisting) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // אם כבר פתח תקופה
  if (hasOpenedToday && !showSummaryView) {
    return (
      <div className="p-6">
        <PeriodOpeningSummaryView 
          periodOpening={todayOpening}
          tasks={allRelevantTasks}
          onContinueToSummary={() => navigate('/period-summary')}
          onChangePeriod={(newPeriod) => {
  setPeriodType(newPeriod);
  setHasOpenedToday(false);
  setCheckingExisting(true);
}}
          onBackToEdit={() => {
            setHasOpenedToday(false);
            
          }}
        />
      </div>
    );
  }

  // טופס פתיחת מחזור
  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-xl flex items-center justify-center">
            {getPeriodIcon(periodType)}
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              פתיחת יום/מחזור
            </h1>
            <p className="text-gray-600">
              בואו נתכנן את ה{getPeriodTitle(periodType)} יחד! 🚀
            </p>
          </div>
        </div>
        
        {/* Period Type Selector */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            בחר סוג תקופה:
          </label>
          <div className="flex gap-2 flex-wrap">
            {['day', 'week', 'month', 'quarter', 'year'].map(type => (
              <button
                key={type}
                type="button"
                onClick={() => {
                  setPeriodType(type);
                  setHasOpenedToday(false);
                  setCheckingExisting(true);
                }}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  periodType === type
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {getPeriodTitle(type)}
              </button>
            ))}
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-800">
            <Calendar className="w-5 h-5" />
            <span className="font-medium">
              {new Date().toLocaleDateString('he-IL', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-8">
        
        {/* סקירת התקופה */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <Calendar className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold">
              {periodType === 'day' ? 'מה קורה היום?' :
               periodType === 'week' ? 'תכנון השבוע' :
               periodType === 'month' ? 'יעדי החודש' :
               periodType === 'quarter' ? 'תכנית הרבעון' :
               'חזון השנה'}
            </h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                סקירה כללית של ה{getPeriodTitle(periodType)}
              </label>
              <textarea
                value={formData.overview}
                onChange={(e) => handleInputChange('overview', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={periodType === 'day' ? "3" : "4"}
                placeholder={
                  periodType === 'day' ? "מה מתוכנן להיום? איך אתה מרגיש לקראת היום?" :
                  periodType === 'week' ? "מה היעדים העיקריים לשבוע? מה צריך להשיג?" :
                  periodType === 'month' ? "מהם היעדים המרכזיים לחודש? אילו פרויקטים בפוקוס?" :
                  periodType === 'quarter' ? "מהם היעדים האסטרטגיים לרבעון? מה צריך להתקדם?" :
                  "מה החזון לשנה? אילו יעדים גדולים רוצה להשיג?"
                }
                required
              />
            </div>
          </div>
        </div>

        {/* יעדים ופריוריטיז - לשבוע ומעלה */}
        {periodType !== 'day' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Target className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-semibold">יעדים מרכזיים</h2>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {periodType === 'week' ? '3-5 יעדים לשבוע' :
                   periodType === 'month' ? 'יעדים חודשיים' :
                   periodType === 'quarter' ? 'יעדי OKR לרבעון' :
                   'יעדים שנתיים'}
                </label>
                
                <div className="space-y-2">
                  {formData.goals.map((goal, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                      <span className="flex-1">{goal}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveFromList('goals', index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                  
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="הוסף יעד חדש..."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddToList('goals', e.target.value);
                          e.target.value = '';
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* יעדים מרכזיים - בחירת משימות (רק ליום) */}
        {periodType === 'day' && allRelevantTasks.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-semibold">יעדים מרכזיים להיום</h2>
              <span className="text-sm text-gray-500">בחר 3-5 משימות שתתמקד בהן היום</span>
            </div>
            
            <div className="space-y-6">
              {personalTasks.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <h3 className="font-semibold text-gray-800">משימות אישיות</h3>
                    <span className="text-sm text-gray-500">({personalTasks.length} משימות)</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {personalTasks.map(task => (
                      <div 
                        key={task.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          formData.selectedTasks.includes(task.id)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                        onClick={() => handleTaskToggle(task.id)}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData.selectedTasks.includes(task.id)}
                            onChange={() => handleTaskToggle(task.id)}
                            className="w-4 h-4 text-blue-600"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{task.title}</p>
                            <p className="text-sm text-gray-500">
                              {task.priority} • {task.status}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {managerialTasks.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                    <h3 className="font-semibold text-gray-800">משימות ניהוליות</h3>
                    <span className="text-sm text-gray-500">({managerialTasks.length} משימות)</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {managerialTasks.map(task => (
                      <div 
                        key={task.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          formData.selectedTasks.includes(task.id)
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                        onClick={() => handleTaskToggle(task.id)}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={formData.selectedTasks.includes(task.id)}
                            onChange={() => handleTaskToggle(task.id)}
                            className="w-4 h-4 text-purple-600"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{task.title}</p>
                            <p className="text-sm text-gray-500">
                              {task.priority} • {task.status}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* פגישות ודדליינים - ליום ושבוע */}
        {(periodType === 'day' || periodType === 'week') && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  פגישות מתוכננות
                </label>
                <textarea
                  value={formData.meetings}
                  onChange={(e) => handleInputChange('meetings', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="2"
                  placeholder={periodType === 'day' ? "רשום פגישות חשובות להיום..." : "פגישות מרכזיות לשבוע..."}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  דדליינים חשובים
                </label>
                <textarea
                  value={formData.deadlines}
                  onChange={(e) => handleInputChange('deadlines', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows="2"
                  placeholder={periodType === 'day' ? "מה חייב להסתיים היום?" : "דדליינים השבוע..."}
                />
              </div>
            </div>
          </div>
        )}

        {/* פרויקטים מרכזיים - לחודש ומעלה */}
        {(periodType === 'month' || periodType === 'quarter' || periodType === 'year') && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Briefcase className="w-6 h-6 text-indigo-600" />
              <h2 className="text-xl font-semibold">פרויקטים מרכזיים</h2>
            </div>
            
            <div className="space-y-2">
              {formData.projects.map((project, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-indigo-50 rounded-lg">
                  <span className="flex-1">{project}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFromList('projects', index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              ))}
              
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="הוסף פרויקט..."
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddToList('projects', e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* KPIs - לרבעון ושנה */}
        {(periodType === 'quarter' || periodType === 'year') && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <BarChart className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold">מדדי הצלחה (KPIs)</h2>
            </div>
            
            <div className="space-y-2">
              {formData.kpis.map((kpi, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                  <span className="flex-1">{kpi}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFromList('kpis', index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              ))}
              
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="הוסף מדד הצלחה..."
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddToList('kpis', e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* חסמים ובעיות */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-red-600" />
            <h2 className="text-xl font-semibold">חסמים ובעיות צפויות</h2>
          </div>
          
          <textarea
            value={formData.blockers}
            onChange={(e) => handleInputChange('blockers', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
            rows="3"
            placeholder={
              periodType === 'day' ? "האם יש בעיות שצריך לפתור? חסמים שעלולים להפריע?" :
              periodType === 'week' ? "אתגרים צפויים השבוע? מה עלול לעכב?" :
              periodType === 'month' ? "חסמים פוטנציאליים החודש? סיכונים לפרויקטים?" :
              periodType === 'quarter' ? "אתגרים אסטרטגיים ברבעון? נקודות תורפה?" :
              "סיכונים מרכזיים לשנה? אתגרים צפויים?"
            }
          />
        </div>

        {/* מצב רוח ואנרגיה - רק ליום */}
        {periodType === 'day' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Heart className="w-6 h-6 text-pink-600" />
              <h2 className="text-xl font-semibold">מצב רוח ואנרגיה</h2>
              <span className="text-sm text-gray-500">(אופציונלי)</span>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  איך אתה מרגיש היום? (1-5)
                </label>
                <div className="flex items-center gap-4">
                  {[1, 2, 3, 4, 5].map(level => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => handleInputChange('moodEnergy', level)}
                      className={`w-12 h-12 rounded-full border-2 flex items-center justify-center font-bold transition-all ${
                        formData.moodEnergy === level
                          ? 'border-pink-500 bg-pink-100 text-pink-700'
                          : 'border-gray-300 text-gray-500 hover:border-pink-300'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>😞 נמוך</span>
                  <span>😐 בינוני</span>
                  <span>😊 גבוה</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* כפתור שמירה */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
          >
            ביטול
          </button>
          
          <button
            type="submit"
            disabled={loading}
            className="px-8 py-3 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-lg hover:from-orange-600 hover:to-yellow-600 font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                <Save className="w-5 h-5" />
                שמור פתיחת {getPeriodTitle(periodType)}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PeriodOpening;